import React, { useState } from 'react';
import styles from './ShotView.module.scss';
import { IconButton, SpriteFrame } from 'components';
import { icons } from 'assets';
import ShotCutter from './ShotCutter';
import { inject } from 'mobx-react';
import { EDITOR_SHOTS_STORE, EDITOR_GROUPS_STORE } from 'store';
import { observer } from 'mobx-react-lite';
import { Icon } from 'components/common';
import { ShotViewProps } from './types';

const ShotView = (props: ShotViewProps) => {
  const {
    [EDITOR_SHOTS_STORE]: {
      mergeShots,
      updateShot,
      isShotSelected,
      switchShotSelection,
      switchSelectionOfShotForLocationChange,
      isShotSelectedForLocationChange,
    },
    [EDITOR_GROUPS_STORE]: {
      createNewGroup
    },
    shot,
    nextShotId = null,
    availableForGroup = false,
    hideActions = false,
    selectable = false,
    singleSelect = false,
    markIfSelected = true,
    isSelected = markIfSelected && isShotSelected(shot),
    onClick = (_) => selectable && switchShotSelection(shot.id, singleSelect),
    className = ''
  } = props;

  const [showShotCutter, setShowShotCutter] = useState(false);
  const containerStyles = [styles.container];
  selectable && containerStyles.push(styles.cursorPointer);
  className && containerStyles.push(className);
  const isShotAvailableForLocationChange = !shot.name || shot.name === '<1sec';
  const isSelectedForLocationChange = isShotSelectedForLocationChange(shot.id);

  return (
    <>
      <div
        className={containerStyles.join(' ')}
        onClick={onClick}
      >
        {/* {console.count('SHOT View')} */}
        <div className={styles.framesContainer}>
          <SpriteFrame frameNumber={shot.startFrame} />
          <SpriteFrame frameNumber={shot.endFrame} />
          {isSelected && <div className={styles.selectedMark} />}
          {isSelectedForLocationChange &&
            <div className={styles.selectedForLocationChangeMark}>
              <Icon
                icon={icons.arrowFillRight}
              />
              <Icon
                icon={icons.locationPinInCircle}
              />
            </div>
          }
          <span>{shot.startFrame}</span>
          <span>{shot.endFrame}</span>
        </div>
        {nextShotId &&
          <IconButton
            icon={icons.mergeArrows}
            className={[styles.icon, styles.mergeIcon].join(' ')}
            title='Merge shots'
            onClick={() => mergeShots(shot.id, nextShotId)}
          />
        }

        {!hideActions &&
          <div className={styles.actionsContainer}>
            {shot.name
              ? <IconButton
                icon={icons.folder}
                className={[styles.icon, styles.staticHover].join(' ')}
              />
              : <IconButton
                icon={icons.openedFolder}
                className={styles.icon}
                disabled={!availableForGroup}
                title='Add this shot to the selected group'
                onClick={() => updateShot(shot, true)}
              />
            }
            <div>{shot.name || 'ungrouped'}</div>
            {isShotAvailableForLocationChange &&
              <IconButton
                icon={isSelectedForLocationChange ? icons.checkedRect : icons.uncheckedRect}
                className={`${styles.icon} ${styles.iconCheckbox}`}
                title='Select shot to change its location'
                onClick={() => switchSelectionOfShotForLocationChange(shot.id)}
              />}
            <IconButton
              icon={icons.scissors}
              className={styles.icon}
              title='Split this shot'
              onClick={() => setShowShotCutter(true)}
            />
            {!shot.name && <IconButton
              icon={icons.plus}
              className={styles.icon}
              title='Create new group from this shot'
              onClick={() => createNewGroup(shot.id)}
            />}
            {shot.name &&
              (availableForGroup
                ? <IconButton
                  icon={icons.recycle}
                  className={styles.icon}
                  title='Move this shot to Selected Group'
                  onClick={() => updateShot(shot, true)}
                />
                : <IconButton
                  icon={icons.shareFrom}
                  className={styles.icon}
                  title='Remove this shot from Current Group'
                  onClick={() => updateShot({ ...shot, name: null })}
                />)
            }
          </div>
        }

      </div>
      {
        showShotCutter &&
        <ShotCutter
          shot={shot}
          onClose={() => setShowShotCutter(false)}
        />
      }
    </>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.nextShotId !== nextProps.nextShotId) return false;
  if (prevProps.availableForGroup !== nextProps.availableForGroup) return false;


  const errMessage = 'Shot is updated.';
  try {
    Object.keys(prevProps.shot).forEach(key => {
      if (prevProps.shot[key] !== nextProps.shot[key]) {
        throw new Error(errMessage);
      }
    });
  } catch (e) {
    if (e.message === errMessage) return false;
    throw e;
  }

  return true;
};

export default React.memo(inject(EDITOR_SHOTS_STORE, EDITOR_GROUPS_STORE)(observer(ShotView)), arePropsEqual);
